import { useEffect, useState } from "react";
import { Crown, TrendingUp, DollarSign, Users, Calendar, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { PortalShell } from "@/components/PortalShell";

function Metric({ label, value, hint, icon: Icon, accent = "cyan" }) {
  const styles = {
    cyan: "text-cyan-700 bg-cyan-50",
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    rose: "text-rose-700 bg-rose-50",
    slate: "text-slate-700 bg-slate-100",
  }[accent];
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-5">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${styles}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

const STATUS_PILL = {
  active:    { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
  cancelled: { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500" },
  expired:   { bg: "bg-slate-100",   text: "text-slate-700",   dot: "bg-slate-400" },
  failed:    { bg: "bg-rose-100",    text: "text-rose-800",    dot: "bg-rose-500" },
  inactive:  { bg: "bg-slate-100",   text: "text-slate-700",   dot: "bg-slate-400" },
};

export default function AdminMemberships() {
  const [metrics, setMetrics] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderRunning, setReminderRunning] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  const load = () => {
    Promise.all([
      api.get("/memberships/admin/metrics").then(({ data }) => setMetrics(data)),
      api.get("/memberships/admin/list").then(({ data }) => setList(data)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const triggerReminders = async (dryRun) => {
    setReminderRunning(true);
    try {
      const { data } = await api.post(`/memberships/admin/reminders/run?dry_run=${dryRun}`);
      setReminderResult(data);
    } finally {
      setReminderRunning(false);
    }
  };

  return (
    <PortalShell>
      <div data-testid="admin-memberships-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Recurring Revenue</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">AWARE Water Health Record Memberships</h1>
            <p className="text-slate-500 mt-1 text-sm">Annual $99/yr subscriptions, savings tracking, renewal reminders.</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-mb-reminders-dry"
              onClick={() => triggerReminders(true)}
              disabled={reminderRunning}
              className="text-xs px-3.5 py-2 rounded-full bg-white border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50 font-semibold text-slate-700"
            >
              {reminderRunning ? "Running..." : "Dry-run reminders"}
            </button>
            <button
              data-testid="admin-mb-reminders-run"
              onClick={() => triggerReminders(false)}
              disabled={reminderRunning}
              className="text-xs px-3.5 py-2 rounded-full bg-slate-900 hover:bg-cyan-700 text-white font-semibold"
            >
              {reminderRunning ? "Sending..." : "Send reminders now"}
            </button>
          </div>
        </div>

        {reminderResult && (
          <div data-testid="admin-mb-reminder-result" className="mt-4 rounded-2xl bg-cyan-50 border border-cyan-200 p-4 text-sm text-cyan-900">
            <span className="font-semibold">Result:</span> <code className="font-mono text-xs">{JSON.stringify(reminderResult)}</code>
          </div>
        )}

        {/* Metrics grid */}
        {metrics && (
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-mb-metrics">
            <Metric label="Active Members" value={metrics.active_members} hint="Currently paid up" icon={Users} accent="emerald" />
            <Metric label="Cancelled (will lapse)" value={metrics.cancelled_members} hint="Cancel at period end" icon={AlertTriangle} accent="amber" />
            <Metric label="Expired" value={metrics.expired_members} hint="Subscription ended" icon={Calendar} accent="slate" />
            <Metric label="Payment Failed" value={metrics.failed_members} hint="Past due / unpaid" icon={AlertTriangle} accent="rose" />
            <Metric label="MRR" value={`$${metrics.mrr?.toFixed(2)}`} hint="Annual ÷ 12" icon={DollarSign} accent="cyan" />
            <Metric label="ARR" value={`$${metrics.arr?.toFixed(2)}`} hint="Annual recurring revenue" icon={TrendingUp} accent="cyan" />
            <Metric label="Renewals due (30d)" value={metrics.renewals_due_30d} hint="Auto-renewals coming up" icon={Calendar} accent="amber" />
            <Metric label="Member LTV" value={`$${metrics.ltv?.toFixed(2)}`} hint={`Renewal rate: ${metrics.renewal_rate != null ? metrics.renewal_rate + "%" : "—"}`} icon={CheckCircle2} accent="emerald" />
          </div>
        )}

        {/* Members table */}
        <div className="mt-8">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-xl font-bold text-slate-900">All members</h2>
            <span className="text-xs text-slate-500">{list.length} total</span>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">Member</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Renews / Ends</th>
                    <th className="text-left px-5 py-3">Days left</th>
                    <th className="text-right px-5 py-3">Savings YTD</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-10 text-slate-500">Loading...</td></tr>
                  ) : list.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-10 text-slate-500">
                      <Crown className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                      No memberships yet — first signup will appear here.
                    </td></tr>
                  ) : (
                    list.map((m) => {
                      const pill = STATUS_PILL[m.derived_status] || STATUS_PILL.inactive;
                      const end = formatDateAU(m.current_period_end);
                      const isOverdue = m.days_until_renewal != null && m.days_until_renewal < 7 && m.days_until_renewal >= 0;
                      return (
                        <tr key={m.stripe_subscription_id || m.user_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                          <td className="px-5 py-3">
                            <div className="font-medium text-slate-900">{m.user_full_name || "—"}</div>
                            <div className="text-xs text-slate-500">{m.user_email}</div>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`} />
                              {m.derived_status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-slate-700">{end}</td>
                          <td className={`px-5 py-3 ${isOverdue ? "text-amber-700 font-semibold" : "text-slate-700"}`}>
                            {m.days_until_renewal == null ? "—" : `${m.days_until_renewal}d`}
                          </td>
                          <td className="px-5 py-3 text-right font-mono">${(m.savings_total || 0).toFixed(2)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
